import React from 'react';

// Tiny markdown subset matching the hub's renderBody()/renderInline()
// (frontend/src/pages/Newsletters.jsx). NOT full CommonMark.
//
// Blocks (separated by a blank line):
//   "## x"  -> h2
//   "### x" -> h3
//   every line starts with "- " or "* " -> <ul><li>…</li></ul>
//   anything else -> <p>
// Inline (recursive): **bold**, *italic*, [text](url)

// Ordered by priority so that, at an equal start index, bold beats italic
// (so "**x**" is never mis-read as "*x*").
const INLINE = [
  { type: 'bold', re: /\*\*([\s\S]+?)\*\*/ },
  { type: 'link', re: /\[([^\]]+)\]\(([^)\s]+)\)/ },
  { type: 'italic', re: /\*([^*\n]+?)\*/ },
];

export function renderInline(text, keyPrefix = 'i') {
  if (!text) return [];
  const nodes = [];
  let rest = text;
  let counter = 0;

  while (rest.length > 0) {
    let best = null;
    for (const pat of INLINE) {
      const m = pat.re.exec(rest);
      if (m && (best === null || m.index < best.m.index)) {
        best = { pat, m };
      }
    }

    if (!best) {
      nodes.push(rest);
      break;
    }

    const { pat, m } = best;
    if (m.index > 0) nodes.push(rest.slice(0, m.index));

    const key = `${keyPrefix}-${counter++}`;
    if (pat.type === 'bold') {
      nodes.push(<strong key={key}>{renderInline(m[1], key)}</strong>);
    } else if (pat.type === 'italic') {
      nodes.push(<em key={key}>{renderInline(m[1], key)}</em>);
    } else if (pat.type === 'link') {
      nodes.push(
        <a key={key} href={m[2]} target="_blank" rel="noopener noreferrer">
          {renderInline(m[1], key)}
        </a>
      );
    }

    rest = rest.slice(m.index + m[0].length);
  }

  return nodes;
}

function isListBlock(block) {
  const lines = block.split('\n').filter((l) => l.trim() !== '');
  return lines.length > 0 && lines.every((l) => /^\s*[-*]\s+/.test(l));
}

export function renderBody(markdown) {
  if (!markdown) return null;
  const blocks = markdown.replace(/\r\n/g, '\n').split(/\n{2,}/);
  const out = [];

  blocks.forEach((raw, bi) => {
    const block = raw.trim();
    if (!block) return;
    const key = `b-${bi}`;

    if (block.startsWith('## ')) {
      out.push(<h2 key={key}>{renderInline(block.slice(3).trim(), key)}</h2>);
    } else if (block.startsWith('### ')) {
      out.push(<h3 key={key}>{renderInline(block.slice(4).trim(), key)}</h3>);
    } else if (isListBlock(block)) {
      const items = block.split('\n').filter((l) => l.trim() !== '');
      out.push(
        <ul key={key}>
          {items.map((line, li) => {
            const content = line.replace(/^\s*[-*]\s+/, '');
            return <li key={`${key}-${li}`}>{renderInline(content, `${key}-${li}`)}</li>;
          })}
        </ul>
      );
    } else {
      out.push(<p key={key}>{renderInline(block, key)}</p>);
    }
  });

  return out;
}

// Convenience component.
export default function Markdown({ children, className = '' }) {
  return <div className={`markdown ${className}`.trim()}>{renderBody(children)}</div>;
}
